import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useActivityFeed } from '@/hooks';
import { formatCompactTime } from '@/lib/utils';
import type { ProjectPhase } from '@/types';

/**
 * Map project phase to badge variant and label
 */
const getActivityBadge = (phase: ProjectPhase): { variant: 'dev' | 'idea' | 'vote' | 'fund'; label: string } => {
  switch (phase) {
    case 'DEVELOPMENT':
      return { variant: 'dev', label: 'DEV' };
    case 'IDEATION':
      return { variant: 'idea', label: 'IDEA' };
    case 'PRESENTATION':
      return { variant: 'vote', label: 'VOTE' };
    case 'DELIVERED':
      return { variant: 'fund', label: 'FUND' };
    case 'ARCHIVED':
    default:
      return { variant: 'fund', label: 'FUND' };
  }
};

/**
 * Truncate content to a maximum length
 */
const truncateContent = (content: string, maxLength: number = 80): string => {
  // Remove markdown formatting for preview
  const cleaned = content
    .replace(/[#*`_~\[\]]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.slice(0, maxLength).trim() + '...';
};

/**
 * Live Feed Component
 * Displays real-time activity feed from across all projects
 */
const LiveFeed = () => {
  const { activities, isLoading, error } = useActivityFeed(5);

  return (
    <Card className="bg-card border-border p-4 w-full max-w-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-green-500 animate-pulse" />
          <h3 className="font-semibold text-foreground">Live Feed</h3>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground tracking-wider">
          REAL-TIME
        </span>
      </div>

      {/* Activity List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <span className="text-sm text-muted-foreground animate-pulse">
              Loading activity...
            </span>
          </div>
        ) : error ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            Unable to load activity
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            No recent activity
          </div>
        ) : (
          activities.map((activity) => {
            const badge = getActivityBadge(activity.project.phase);
            return (
              <Link
                key={activity.thread.id}
                to={`/project/${activity.project.id}`}
                className="block group"
              >
                <div className="space-y-1">
                  {/* User + Badge + Time */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-primary text-sm font-medium">
                        @{activity.thread.createdBy.name}
                      </span>
                      <Badge variant={badge.variant}>
                        {badge.label}
                      </Badge>
                    </div>
                    <span className="text-muted-foreground text-xs">
                      {formatCompactTime(activity.thread.timestamp)}
                    </span>
                  </div>
                  
                  {/* Project Title */}
                  <h4 className="text-foreground font-semibold text-sm group-hover:text-primary transition-colors">
                    {activity.project.title}
                  </h4>
                  
                  {/* Content Preview */}
                  <p className="text-muted-foreground text-xs font-mono leading-relaxed line-clamp-2">
                    {truncateContent(activity.thread.content)}
                  </p>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default LiveFeed;
